/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { EntityState } from '@reduxjs/toolkit';
import type { InitialProps, SupportedComponentTypes } from '@opengeoweb/core';
import type { PluginComponentType, PluginMeta } from '@opengeoweb/plugins';
import type { SystemScope } from '@opengeoweb/shared';
import type { SoundingsComponentType } from '@opengeoweb/soundings';
import type { SupportedComponentTypes as SupportedTimeSeriesComponentTypes } from '@opengeoweb/time-series';
import type { WarningListComponentType } from '@opengeoweb/warnings';
import type {
  ViewPresetListItem,
  WorkspaceLabelsConfigResponse,
  WorkspacePresetLabels,
  WorkspaceViewPresetComponentsLookupType,
} from '../workspace/types';
import type { PresetFilterType } from '../workspaceList/types';

export const AVIATION_COMPONENT_TYPES = [
  'AirmetModule',
  'SigmetModule',
] as const;

export type AviationComponentType = (typeof AVIATION_COMPONENT_TYPES)[number];

export const CAP_COMPONENT_TYPES = ['CapWarningList', 'CapWarningMap'] as const;

export type CapComponentType = (typeof CAP_COMPONENT_TYPES)[number];

// 1) Map-like components
export const SUPPORTED_COMPONENT_TYPES = [
  'Map',
  'MultiMap',
  'ModelRunInterval',
  'HarmonieTempAndPrecipPreset',
  'TimeSlider',
] as const;

// If you can change the existing type, do this:
// export type SupportedComponentTypes = (typeof SUPPORTED_COMPONENT_TYPES)[number];

// If you *must* keep the old type definition, just ensure it matches the array:
export type SupportedMapComponentTypes =
  | 'Map'
  | 'MultiMap'
  | 'ModelRunInterval'
  | 'HarmonieTempAndPrecipPreset'
  | 'TimeSlider';

// 2) TimeSeries components
export const SUPPORTED_TIMESERIES_COMPONENT_TYPES = ['TimeSeries'] as const;

// Same note as above:
export type SupportedTSComponentTypes =
  (typeof SUPPORTED_TIMESERIES_COMPONENT_TYPES)[number];
// or keep your old union type if it already exists

// 3) Warning list components
export const WARNING_LIST_COMPONENT_TYPES = [
  'WarningList',
  'MaritimeWarnings',
  'WarningsTimeline',
] as const;

export type WarningsComponentType =
  (typeof WARNING_LIST_COMPONENT_TYPES)[number];

export type WorkspaceSupportedComponentTypes =
  | SupportedComponentTypes
  | SupportedTimeSeriesComponentTypes
  | SoundingsComponentType
  | WarningListComponentType
  | WorkspaceViewPresetComponentsLookupType
  | PluginComponentType;

export type LabelSupportedComponentTypes =
  | SupportedComponentTypes
  | SupportedTimeSeriesComponentTypes
  | SoundingsComponentType
  | WarningListComponentType
  | AviationComponentType
  | CapComponentType
  | 'TafModule'
  | 'TrajksModule'
  | 'SpaceweatherModule'
  | 'Plugin'
  | WorkspaceViewPresetComponentsLookupType;

// TODO: expand it and move to a more centralized place
export const ALL_COMPONENT_TYPES: readonly LabelSupportedComponentTypes[] = [
  ...SUPPORTED_COMPONENT_TYPES,
  ...SUPPORTED_TIMESERIES_COMPONENT_TYPES,
  ...WARNING_LIST_COMPONENT_TYPES,
  ...AVIATION_COMPONENT_TYPES,
  ...CAP_COMPONENT_TYPES,
  'TafModule',
  'TrajksModule',
  'SpaceweatherModule',
  'Plugin',
  'Soundings',
] as const;

// ViewPreset coming from BE
export interface ViewPreset {
  id?: string;
  title: string;
  description?: string;
  abstract?: string;
  keywords: string;
  componentType: WorkspaceSupportedComponentTypes;
  initialProps?: InitialProps;
  scope?: SystemScope;
  is_shared?: boolean;
  username?: string;
  labels?: WorkspacePresetLabels;
}

export enum ViewPresetErrorComponent {
  PRESET_DETAIL = 'PRESET_DETAIL',
  PRESET_LIST = 'PRESET_LIST',
}

export enum ViewPresetErrorType {
  AUTH = 'AUTH', // Errors related to authentication and authorization
  GENERIC = 'GENERIC',
}

export type ViewPresetError =
  | {
      component: ViewPresetErrorComponent;
      errorType: ViewPresetErrorType;
      message: string;
    }
  | undefined;

export interface ViewPresetsListFilter {
  label: string;
  id: PresetFilterType | 'all';
  isSelected?: boolean;
  isDisabled?: boolean;
}

export interface ViewPresetEntity {
  panelId: string; // can be a mapId or a viewId because 1 view can have multiple maps, but some views as sigmet don't have a map
  activeViewPresetId: string;
  hasChanges: boolean;
  isFetching: boolean;
  error: ViewPresetError;
  isViewPresetListDialogOpen: boolean;
  filters: ViewPresetsListFilter[];
  searchQuery: string;
  filterResults: EntityState<ViewPresetListItem, string>;
  labelFilters?: WorkspacePresetLabels;
  username?: string;
}

export interface ViewPresetFormValues {
  title?: string;
  description?: string;
  abstract?: string;
  initialProps?: InitialProps;
  labels?: WorkspaceLabelsConfigResponse;
  componentType?: WorkspaceSupportedComponentTypes;
  updated?: string;
  updatedBy?: string;
  username?: string;
}

export interface ViewPresetDialog {
  title: string; // dialog title
  action: PresetAction;
  viewPresetId: string;
  panelId: string;
  formValues: ViewPresetFormValues;
  shouldSetActiveViewPreset?: boolean;
}

export interface ViewPresetState extends EntityState<ViewPresetEntity, string> {
  viewPresetDialog?: ViewPresetDialog;
}

export enum PresetAction {
  OPEN = 'Open',
  DELETE = 'Delete',
  SAVE = 'Save',
  SAVE_AS = 'Save as',
  SHARE = 'Share',
  EDIT = 'Edit',
  RESET = 'Reset',
}

// actions
interface ViewPresetAction {
  panelId: string;
}

// list api request actions
export type FetchViewPresetsPayload = ViewPresetAction;
export interface FetchedViewPresetsPayload extends ViewPresetAction {
  viewPresets: ViewPresetListItem[];
}

// detail api request actions
export interface FetchViewPresetPayload extends ViewPresetAction {
  viewPresetId: string;
}
export interface FetchedViewPresetPayload extends ViewPresetAction {
  viewPresetId: string;
  viewPreset: ViewPreset;
}
export interface SaveViewPresetPayload extends ViewPresetAction {
  viewPresetId: string;
  viewPreset: ViewPreset;
}
export interface SavedViewPresetPayload extends ViewPresetAction {
  viewPresetId: string;
}
export interface ErrorViewPresetPayload extends ViewPresetAction {
  viewPresetId?: string;
  error: ViewPresetError;
}

// map actions
export interface SetViewPresetHasChangePayload extends ViewPresetAction {
  hasChanges: boolean;
}

export interface SetActiveViewPresetIdPayload extends ViewPresetAction {
  viewPresetId: string;
}

export interface SetViewPresetInlineOverridePayload extends ViewPresetAction {
  isInline: boolean;
}

export interface OnSuccesViewPresetActionPayload extends ViewPresetAction {
  viewPresetId: string;
  activeViewPresetId?: string;
  shouldSetActiveViewPreset?: boolean;
  action: PresetAction;
  title: string;
  description?: string;
  componentType?: string;
  scope?: SystemScope;
  abstract?: string;
  is_shared?: boolean;
  labels?: WorkspacePresetLabels;
  username?: string;
}
export interface SelectViewPresetPayload extends ViewPresetAction {
  viewPresetId: string;
  shouldReset?: boolean;
}

export interface SelectPluginPayload extends ViewPresetAction {
  pluginMetaData?: PluginMeta;
}

export interface RegisterViewPresetPayload extends ViewPresetAction {
  viewPresetId: string;
  filters?: ViewPresetsListFilter[];
}

export interface ToggleViewPresetListDialogPayload extends ViewPresetAction {
  isViewPresetListDialogOpen: boolean;
}

export interface ToggleSelectFilterChip extends ViewPresetAction {
  id: PresetFilterType;
  isSelected: boolean;
  isAdmin?: boolean;
}

export interface SetFilterChipsPayload extends ViewPresetAction {
  filters: {
    id: PresetFilterType;
    isSelected: boolean;
  }[];
}

export type SetSelectAllFilterChip = ViewPresetAction;

export interface SearchWorkspaceFilter extends ViewPresetAction {
  searchQuery: string;
}

export type UnregisterViewPresetPayload = ViewPresetAction;

// dialog options
export interface OpenViewPresetDialogPayload {
  viewPresetDialog: ViewPresetDialog;
}

export interface SetViewPresetLabelFiltersPayload extends ViewPresetAction {
  labels: WorkspacePresetLabels;
}

export type ClearViewPresetLabelFiltersPayload = ViewPresetAction;
